/**
 * AdminFeedbackPage — founder-gated print-friendly viewer for
 * /api/validator-popup-feedback/list.
 *
 * Flow:
 *   1. Page loads → prompts for admin passcode (default `sivam`).
 *   2. On submit → calls GET /api/validator-popup-feedback/list with
 *      header `x-admin-passcode: <code>`.
 *   3. Renders rows as a print-friendly table. Each row shows validator
 *      identity, encounter ID, all 8 answers, free-text notes, timestamp.
 *   4. Print button uses window.print() for clean PDF export.
 *
 * Passcode is NEVER persisted client-side beyond the page session.
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Printer, RefreshCw, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANSWER_LABELS = {
  q1: { yes: "Yes", no: "No", mostly: "Mostly" },
  q2: { no: "No", yes_short_note: "Yes (note)" },
  q3: { game_changer: "Game-changer", useful: "Useful", neutral: "Neutral", noise: "Noise" },
  q4: { yes_meaningful: "Yes — meaningful", no_llm_fine: "No — LLM is fine", dont_know: "Don't know" },
  q5: { strongly_support: "Strongly support", support: "Support", neutral: "Neutral", concerned: "Concerned" },
  q7: { major_time_saver: "Major time-saver", useful_some: "Useful for some", neutral: "Neutral", no_change: "No change" },
  q8: { yes_adopt: "Yes — adopt", yes_possibly: "Yes — possibly", dont_teach: "Don't teach", not_relevant: "Not relevant" },
};

const QUESTION_HEADERS = [
  { key: "q1", short: "Q1 · SOAP appropriate?" },
  { key: "q2", short: "Q2 · Missed/wrong flag?" },
  { key: "q3", short: "Q3 · Pre-organized PMH value?" },
  { key: "q4", short: "Q4 · Deterministic > LLM?" },
  { key: "q5", short: "Q5 · Free patient access?" },
  { key: "q7", short: "Q7 · Pre-visit briefing?" },
  { key: "q8", short: "Q8 · Teaching use?" },
];

const fmtAnswer = (qKey, v) => (v == null ? "—" : ANSWER_LABELS[qKey]?.[v] ?? v);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function AdminFeedbackPage() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchRows = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/validator-popup-feedback/list`, {
        headers: { "x-admin-passcode": code, "X-System-Test": "pai-system-test-2026" },
      });
      setRows(r.data.feedback || []);
      setTotal(r.data.total || 0);
      setAuthed(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError("Wrong passcode.");
      } else {
        setError(e?.response?.data?.error || e.message || "Unable to load.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [linkForm, setLinkForm] = useState({ name: "", email: "", specialty: "" });
  const [copied, setCopied] = useState(false);

  const specialties = Array.from(
    new Set(rows.map((r) => (r.validator_specialty || "").trim()).filter(Boolean))
  ).sort();
  const filteredRows =
    specialtyFilter === "all"
      ? rows
      : rows.filter((r) => (r.validator_specialty || "").trim() === specialtyFilter);

  const inviteLink = (() => {
    const base = `${window.location.origin}/register/validator`;
    const p = new URLSearchParams();
    if (linkForm.name.trim()) p.set("name", linkForm.name.trim());
    if (linkForm.email.trim()) p.set("email", linkForm.email.trim());
    if (linkForm.specialty.trim()) p.set("specialty", linkForm.specialty.trim());
    const qs = p.toString();
    return qs ? `${base}?${qs}` : base;
  })();

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* no-op */ }
  };

  const csvEscape = (v) => `"${(v == null ? "" : String(v)).replace(/"/g, '""')}"`;

  const downloadCSV = () => {
    const cols = [
      ["When", (r) => fmtDate(r.created_at)],
      ["Validator", (r) => r.validator_name],
      ["Email", (r) => r.validator_email],
      ["Specialty", (r) => r.validator_specialty],
      ["Encounter", (r) => r.encounter_id],
      ...QUESTION_HEADERS.map((q) => [q.short, (r) => fmtAnswer(q.key, r[q.key])]),
      ["Q2 note", (r) => r.q2_note],
      ["Q6 free text", (r) => r.q6_free_text],
    ];
    const header = cols.map((c) => csvEscape(c[0])).join(",");
    const body = filteredRows.map((r) => cols.map((c) => csvEscape(c[1](r))).join(","));
    const csv = "\ufeff" + [header, ...body].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `validator-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="admin-feedback-gate">
        <div className="bg-white border-2 border-slate-300 rounded-xl shadow-sm p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-slate-700" />
            <h1 className="text-lg font-bold text-slate-900">Validator Feedback — Founder Access</h1>
          </div>
          <p className="text-xs text-slate-600 mb-5">
            Enter the founder admin passcode to view validator popup responses. The passcode is never stored client-side.
          </p>
          <Label className="text-xs font-bold text-slate-800">Admin passcode</Label>
          <Input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && passcode) fetchRows(passcode); }}
            placeholder="••••••"
            className="mt-1"
            data-testid="admin-feedback-passcode-input"
            autoFocus
          />
          {error && (
            <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="admin-feedback-error">
              {error}
            </div>
          )}
          <Button
            onClick={() => passcode && fetchRows(passcode)}
            disabled={!passcode || loading}
            className="mt-5 w-full bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="admin-feedback-unlock-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Unlock
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10" data-testid="admin-feedback-page">
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-wrap items-end justify-between gap-3 mb-6 no-print">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">P.AI Global Health · Founder Admin</div>
            <h1 className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="admin-feedback-title">
              Validator Feedback — {total} {total === 1 ? "response" : "responses"}
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Optional end-of-SOAP survey. Newest first. Synthetic data, no PHI.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {specialties.length > 0 && (
              <select
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700"
                data-testid="admin-feedback-specialty-filter"
              >
                <option value="all">All specialties ({rows.length})</option>
                {specialties.map((s) => (
                  <option key={s} value={s}>{s} ({rows.filter((r) => (r.validator_specialty || "").trim() === s).length})</option>
                ))}
              </select>
            )}
            <Button
              variant="outline"
              onClick={() => fetchRows(passcode)}
              disabled={loading}
              data-testid="admin-feedback-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={downloadCSV}
              disabled={rows.length === 0}
              data-testid="admin-feedback-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
            <Button
              onClick={() => window.print()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="admin-feedback-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </header>

        {/* Feedback Link — generate a pre-filled validator invite */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6 no-print" data-testid="validator-invite-generator">
          <h2 className="text-sm font-bold text-slate-900 mb-1">Validator invite link</h2>
          <p className="text-xs text-slate-500 mb-3">Fill any of these to pre-fill the validator's registration. Send them the link — one click and their details are ready.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <Input placeholder="Name (optional)" value={linkForm.name} onChange={(e) => setLinkForm((p) => ({ ...p, name: e.target.value }))} data-testid="invite-name" />
            <Input placeholder="Email (optional)" value={linkForm.email} onChange={(e) => setLinkForm((p) => ({ ...p, email: e.target.value }))} data-testid="invite-email" />
            <Input placeholder="Specialty (optional)" value={linkForm.specialty} onChange={(e) => setLinkForm((p) => ({ ...p, specialty: e.target.value }))} data-testid="invite-specialty" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-[240px] text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-700 break-all" data-testid="invite-link-value">{inviteLink}</code>
            <Button onClick={copyInvite} variant="outline" data-testid="invite-copy-btn">
              {copied ? "Copied!" : "Copy link"}
            </Button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-slate-500" data-testid="admin-feedback-empty">
            No feedback yet. As soon as a validator submits the popup at the end of their SOAP, the row will appear here.
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-feedback-table">
                <thead>
                  <tr className="bg-slate-900 text-white text-left">
                    <th className="px-3 py-2 font-bold">When</th>
                    <th className="px-3 py-2 font-bold">Validator</th>
                    <th className="px-3 py-2 font-bold">Specialty</th>
                    <th className="px-3 py-2 font-bold">Encounter</th>
                    {QUESTION_HEADERS.map((q) => (
                      <th key={q.key} className="px-3 py-2 font-bold whitespace-nowrap">{q.short}</th>
                    ))}
                    <th className="px-3 py-2 font-bold">Q2 note</th>
                    <th className="px-3 py-2 font-bold">Q6 free text</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.feedback_id} className="border-b border-slate-100 align-top" data-testid={`admin-feedback-row-${r.feedback_id}`}>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <div className="font-bold text-slate-900">{r.validator_name || "—"}</div>
                        <div className="text-slate-500">{r.validator_email || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.validator_specialty || "—"}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{r.encounter_id || "—"}</td>
                      {QUESTION_HEADERS.map((q) => (
                        <td key={q.key} className="px-3 py-2 text-slate-800">{fmtAnswer(q.key, r[q.key])}</td>
                      ))}
                      <td className="px-3 py-2 text-slate-700 italic min-w-[320px] max-w-[520px] whitespace-pre-wrap break-words">{r.q2_note || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 italic min-w-[320px] max-w-[520px] whitespace-pre-wrap break-words">{r.q6_free_text || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[10px] text-slate-400 mt-4 no-print">
          Source: <code>GET /api/validator-popup-feedback/list</code> (passcode-gated). Sorted by created_at descending. Capped at 500 rows.
        </p>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          table { font-size: 9.5px; }
          th { background: #0f172a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
